/**
 * LinkPreviewCard - Displays a rich preview card for URLs
 * Similar to how Slack/Discord shows link previews
 */

import React from 'react';
import { ExternalLink, Globe } from 'lucide-react';
import TrackedLink from './TrackedLink';

const LinkPreviewCard = ({ preview, className = '' }) => {
  if (!preview || !preview.url) return null;

  const { url, title, description, image, site_name, domain, favicon } = preview;

  return (
    <TrackedLink
      href={url}
      source="link_preview"
      className={`block no-underline ${className}`}
      data-testid="link-preview-card"
    >
      <div className="border rounded-lg overflow-hidden bg-card hover:bg-muted/50 transition-colors mt-3">
        {/* Image */}
        {image && (
          <div className="w-full h-48 bg-muted overflow-hidden">
            <img
              src={image}
              alt={title || 'Link preview'}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}
        
        {/* Content */}
        <div className="p-3">
          {/* Site info */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            {favicon ? (
              <img 
                src={favicon} 
                alt="" 
                className="w-4 h-4 rounded"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : null}
            <Globe className="w-4 h-4 hidden" />
            <span>{site_name || domain}</span>
          </div>
          
          {/* Title */}
          {title && (
            <h4 className="font-medium text-foreground line-clamp-2 mb-1">
              {title}
            </h4>
          )}
          
          {/* Description */}
          {description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {description}
            </p>
          )}
          
          {/* URL */}
          <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
            <ExternalLink className="w-3 h-3" />
            <span className="truncate max-w-[250px]">{domain}</span>
          </div>
        </div>
      </div>
    </TrackedLink>
  );
};

export default LinkPreviewCard;
